// File management functions for Firebase Storage
import { getStorage, ref, listAll, uploadBytes, deleteObject, getDownloadURL, getMetadata } from 'firebase/storage'
import app from './config'

const storage = getStorage(app)
const STORAGE_PATH = 'CRM Shared Documents'

/**
 * List all files and folders in the CRM Shared Documents folder
 * @param {string} path - Optional path to list (defaults to STORAGE_PATH)
 * @returns {Promise<Object>} Object with files and folders arrays
 */
export const listFiles = async (path = STORAGE_PATH) => {
  try {
    const folderRef = ref(storage, path)
    const result = await listAll(folderRef)
    
    // Get metadata and download URLs for each file
    const files = await Promise.all(
      result.items.map(async (itemRef) => {
        try {
          const [metadata, downloadURL] = await Promise.all([
            getMetadata(itemRef),
            getDownloadURL(itemRef)
          ])
          
          return {
            name: itemRef.name,
            fullPath: itemRef.fullPath,
            size: metadata.size,
            contentType: metadata.contentType,
            timeCreated: metadata.timeCreated,
            updated: metadata.updated,
            downloadURL,
            isFolder: false
          }
        } catch (error) {
          console.error(`Error getting metadata for ${itemRef.name}:`, error)
          // Return basic info even if metadata fails
          return {
            name: itemRef.name,
            fullPath: itemRef.fullPath,
            size: 0,
            contentType: 'unknown',
            timeCreated: null,
            updated: null,
            downloadURL: null,
            isFolder: false
          }
        }
      })
    )
    
    // Process folders (prefixes)
    const folders = result.prefixes.map((prefixRef) => {
      return {
        name: prefixRef.name,
        fullPath: prefixRef.fullPath,
        size: null,
        contentType: 'folder',
        timeCreated: null,
        updated: null,
        downloadURL: null,
        isFolder: true
      }
    })
    
    // Combine files and folders, sort folders first, then files by creation time (newest first)
    const allItems = [...folders, ...files].sort((a, b) => {
      // Folders first
      if (a.isFolder && !b.isFolder) return -1
      if (!a.isFolder && b.isFolder) return 1
      
      // Then sort by creation time (newest first) for files
      if (!a.isFolder && !b.isFolder) {
        const timeA = a.timeCreated ? new Date(a.timeCreated).getTime() : 0
        const timeB = b.timeCreated ? new Date(b.timeCreated).getTime() : 0
        return timeB - timeA
      }
      
      // Folders sorted alphabetically
      return a.name.localeCompare(b.name)
    })
    
    return allItems
  } catch (error) {
    console.error('Error listing files:', error)
    throw error
  }
}

/**
 * Upload a file to the specified folder
 * @param {File} file - File object to upload
 * @param {string} path - Path to upload to (defaults to STORAGE_PATH)
 * @returns {Promise<Object>} Upload result with file metadata
 */
export const uploadFile = async (file, path = STORAGE_PATH) => {
  try {
    // Create a reference to the file location
    const fileRef = ref(storage, `${path}/${file.name}`)
    
    // Upload the file
    const snapshot = await uploadBytes(fileRef, file)
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref)
    
    // Get metadata
    const metadata = await getMetadata(snapshot.ref)
    
    return {
      name: snapshot.ref.name,
      fullPath: snapshot.ref.fullPath,
      size: metadata.size,
      contentType: metadata.contentType,
      timeCreated: metadata.timeCreated,
      updated: metadata.updated,
      downloadURL
    }
  } catch (error) {
    console.error('Error uploading file:', error)
    throw error
  }
}

/**
 * Delete a file from Firebase Storage
 * @param {string} filePath - Full path to the file (e.g., "CRM Shared Documents/filename.pdf")
 * @returns {Promise<void>}
 */
export const deleteFile = async (filePath) => {
  try {
    const fileRef = ref(storage, filePath)
    await deleteObject(fileRef)
  } catch (error) {
    console.error('Error deleting file:', error)
    throw error
  }
}

/**
 * Delete a folder and all its contents from Firebase Storage
 * @param {string} folderPath - Full path to the folder (e.g., "CRM Shared Documents/folderName")
 * @returns {Promise<void>}
 */
export const deleteFolder = async (folderPath) => {
  try {
    // List all items in the folder
    const folderRef = ref(storage, folderPath)
    const result = await listAll(folderRef)
    
    // Delete all files in the folder
    const deleteFilePromises = result.items.map((itemRef) => deleteObject(itemRef))
    
    // Recursively delete all subfolders
    const deleteFolderPromises = result.prefixes.map((prefixRef) => 
      deleteFolder(prefixRef.fullPath)
    )
    
    // Wait for all deletions to complete
    await Promise.all([...deleteFilePromises, ...deleteFolderPromises])
  } catch (error) {
    console.error('Error deleting folder:', error)
    throw error
  }
}

/**
 * Get download URL for a file
 * @param {string} filePath - Full path to the file
 * @returns {Promise<string>} Download URL
 */
export const getFileDownloadURL = async (filePath) => {
  try {
    const fileRef = ref(storage, filePath)
    return await getDownloadURL(fileRef)
  } catch (error) {
    console.error('Error getting download URL:', error)
    throw error
  }
}
